import { FC, useMemo, useState } from "react"
import { PageTitle, useIntl, Card, Button, Icon, Tab } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams } from "react-router-dom"
import provisionEventStore from "features/provisioning/provisions/stores/provision-event-store"
import customerStore from "features/organizations/customers/store/customer-store"
import { provisionEventsBreadcrumbsGenerator } from "../../provisions-utils/provision-events-bread-crumbs"
import { ProvisionEventEquipment } from "features/provisioning/provisions/models/provision-models"
import { provisionTypeToIcon } from "../../provisions-utils/provision-events-icon"
import ListView from "components/utils/list-view/list-view"
import { provisionEventsLinkGenerator, provisionEventsLinkGeneratorSimple } from "../../provisions-utils/provision-events-link-generator"
import { ProvisionEventModal } from "./provision-events-modal"
import ProvisionEventDeleteModal from "../../provisions-utils/provision-events-delete-modal"
import styles from "./provision-events-asset-view-index.module.css"
import { ProvisionEventsSVGViewer } from "../provision-events-svg-viewer"
import { normaliseEquipmentList, resolveEquipmentElements, sortProvisionEvents } from "../utils/provision-utils"
import { convertDateToDDMMMYY } from "components/utils/date-formatter-utils"
import { Customer } from "features/organizations/customers/models/customer-models"
import { Site } from "features/organizations/sites/models/sites-models"
import { Asset } from "features/organizations/assets/models/asset-models"

const ProvisionEventsAssetView: FC = observer(() => {
    const { customerId, siteId, assetId, assetSectionId, provisionEventId } = useParams()
    if (!customerId || !siteId || !assetId) {
        throw new Error("Customer, Site, and Asset IDs are required")
    }

    const navigate = useNavigate()
    const { formatMessage } = useIntl()
    const { provisionEventsDetail } = provisionEventStore
    const { customerMap, siteMap, assetMap } = customerStore
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [provisionEventIdToEdit, setProvisionEventToEdit] = useState<string>()
    const [isProvisionEventEditModalOpen, setIsProvisionEventEditModalOpen] = useState(false)

    const [provisionEventIdToDelete, setProvisionEventToDelete] = useState<string>()
    const [isProvisionEventDeleteModalOpen, setIsProvisionEventDeleteModalOpen] = useState(false)

    const customer = customerMap[customerId]
    const site = siteMap[siteId]
    const asset = assetMap[assetId]

    const assetSections = asset.assetSections
    const assetSection = assetSections.find(s => s.id === assetSectionId) ?? assetSections[0]
    const indexAssetSection = assetSectionId ? assetSections.findIndex(s => s.id === assetSectionId) : 0

    const sortedProvisionEvents = useMemo(() => sortProvisionEvents(provisionEventsDetail), [provisionEventsDetail])

    const selectedElements = useMemo(() => {
        const filteredEvents = provisionEventId === undefined ? sortedProvisionEvents : provisionEventsDetail.filter(pe => pe.id === provisionEventId)

        const sortedElements = sortProvisionEvents(filteredEvents)
        const elements = sortedElements.map(resolveEquipmentElements).flat()
        return normaliseEquipmentList(elements)
    }, [provisionEventId, sortedProvisionEvents])

    const deleteProvisionEventModal = {
        open: (event: React.MouseEvent, provisionEventId: string) => {
            event.stopPropagation()
            setProvisionEventToDelete(provisionEventId)
            setIsProvisionEventDeleteModalOpen(true)
        },
        close: () => {
            setIsProvisionEventDeleteModalOpen(false)
        },
    }

    const editProvisionEventModal = {
        open: (event: React.MouseEvent, provisionEventId: string) => {
            event.stopPropagation()
            setProvisionEventToEdit(provisionEventId)
            setIsProvisionEventEditModalOpen(true)
        },
        close: () => {
            setIsProvisionEventEditModalOpen(false)
        },
    }

    const breadcrumbsPath = provisionEventsBreadcrumbsGenerator(customer, site, asset, undefined, formatMessage)

    const template = [
        {
            headingKey: "provisionEvents.headings.eventDate",
            cellFunc: (s: ProvisionEventEquipment) => convertDateToDDMMMYY(s.eventDate),
            isSearchable: true,
        },
        {
            headingKey: "provisionEvents.headings.provision",
            cellFunc: (s: ProvisionEventEquipment) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    {provisionTypeToIcon(s.type)}
                    {s.type}
                </span>
            ),
            isSearchable: true,
        },
        {
            headingKey: "provisionEvents.headings.description",
            cellFunc: (s: ProvisionEventEquipment) => s.description,
            isSearchable: true,
        },
        {
            headingKey: "headings.delete",
            cellFunc: (s: ProvisionEventEquipment) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon
                        name='icon-trash-16'
                        onClick={(event: React.MouseEvent<Element, MouseEvent>) => deleteProvisionEventModal.open(event, s.id)}
                    />
                </span>
            ),
        },
        {
            headingKey: "headings.edit",
            cellFunc: (s: ProvisionEventEquipment) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => editProvisionEventModal.open(event, s.id)} />
                </span>
            ),
        },
    ]

    const createLinerMaterialModal = {
        open: () => {
            setIsCreateModalOpen(true)
        },
        close: () => {
            setIsCreateModalOpen(false)
        },
    }

    const renderProvisionEventList = () => {
        const navStub = provisionEventsLinkGeneratorSimple(customer.id, site.id, asset.id, assetSectionId)
        const rowIndex = sortedProvisionEvents.findIndex(s => s.id === provisionEventId)

        return (
            <Card
                className={styles.cardContainer}
                content={
                    <div style={{ width: "100%" }}>
                        <ListView
                            noneYetKey={"provisionEvents.noneYet"}
                            template={template}
                            data={sortedProvisionEvents}
                            onClickRow={(a: ProvisionEventEquipment) => {
                                if (a.id === provisionEventId) {
                                    navigate(`${navStub}`)
                                } else {
                                    navigate(`${navStub}/event/${a.id}`)
                                }
                            }}
                            selectedRow={rowIndex}
                        />
                    </div>
                }
                data-cy='card-provision-events'
                header='Provisioning Events'
            />
        )
    }

    const renderSvg = () => {
        return <ProvisionEventsSVGViewer asset={asset} assetSectionId={assetSection.id} selectedElements={selectedElements} />
    }

    return (
        <>
            <PageTitle
                breadcrumbsPath={breadcrumbsPath}
                onPathChange={navigate}
                title={assetId && assetMap[assetId].name}
                extraItems={
                    <Button icon='icon-plus-16' labelPosition='left' onClick={createLinerMaterialModal.open} primary>
                        {formatMessage("provisionEvents.create")}
                    </Button>
                }
            />
            <div className={styles.tableContainer}>
                <Card
                    className={styles.cardContainer}
                    header={
                        <div style={{ width: "100%" }}>
                            <TabContent
                                asset={asset}
                                customer={customer}
                                indexAssetSection={indexAssetSection}
                                site={site}
                                provisionEventId={provisionEventId}
                            />
                        </div>
                    }
                    headerClassName={styles.tabHeader}
                    content={renderSvg()}
                    data-cy='card-svg-content'
                    style={{ width: "100%" }}
                />

                {renderProvisionEventList()}
                <ProvisionEventModal
                    customerId={customerId}
                    siteId={siteId}
                    assetId={assetId}
                    assetSectionId={assetSection.id}
                    isOpen={isCreateModalOpen}
                    onClose={createLinerMaterialModal.close}
                />
                <ProvisionEventModal
                    customerId={customerId}
                    siteId={siteId}
                    assetId={assetId}
                    assetSectionId={assetSection.id}
                    isOpen={isProvisionEventEditModalOpen}
                    onClose={editProvisionEventModal.close}
                    provisionEvent={provisionEventsDetail.find(pe => pe.id === provisionEventIdToEdit)}
                />
                <ProvisionEventDeleteModal
                    isOpen={isProvisionEventDeleteModalOpen}
                    onClose={deleteProvisionEventModal.close}
                    entity={provisionEventsDetail.find(pe => pe.id === provisionEventIdToDelete)}
                />
            </div>
        </>
    )
})

interface TabContentProps {
    indexAssetSection: number
    customer: Customer
    site: Site
    asset: Asset
    provisionEventId: string | undefined
}

const TabContent = ({ indexAssetSection, customer, site, asset, provisionEventId }: TabContentProps) => {
    const navigate = useNavigate()
    const assetSections = asset.assetSections
    const assetSectionLabels = assetSections.map(s => s.name)

    return (
        <Tab
            as='div'
            data-cy='tab'
            activeIndex={indexAssetSection}
            onTabChange={(e, d) => {
                const nextIndex = Number(d.activeIndex)
                const nextAssetSection = assetSections[nextIndex]
                if (nextAssetSection) {
                    navigate(provisionEventsLinkGenerator(customer, site, asset, nextAssetSection, { id: provisionEventId || null }))
                }
            }}
            menu={{
                pointing: true,
                secondary: true,
            }}
            style={{ width: "100%" }}
            panes={assetSectionLabels.map(asl => {
                return { menuItem: asl }
            })}
        />
    )
}

export default ProvisionEventsAssetView
